export const MONTH_ORDER = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
] as const;

export const WEEK_ORDER = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
] as const;

export type MonthName = (typeof MONTH_ORDER)[number];
export type WeekdayName = (typeof WEEK_ORDER)[number];
export type DateOrderValue = MonthName | WeekdayName;

export const getLogicalIndex = (value: any): number | null => {
  if (typeof value !== 'string') return null;

  const monthIndex = MONTH_ORDER.indexOf(value as MonthName);
  if (monthIndex !== -1) return monthIndex;

  const weekIndex = WEEK_ORDER.indexOf(value as WeekdayName);
  if (weekIndex !== -1) return weekIndex;

  return null;
};

export const isMonthOrWeekday = (value: any): value is DateOrderValue => {
  return getLogicalIndex(value) !== null;
};

export const isMonthOrWeekField = (fieldData: any[], fieldName: string): boolean => {
  if (!Array.isArray(fieldData) || fieldData.length === 0) return false;

  const samplesToCheck = 5;
  let checkedCount = 0;
  let matchCount = 0;

  for (const item of fieldData) {
    if (checkedCount >= samplesToCheck) break;

    const value = item[fieldName];
    if (value !== null && value !== undefined && value !== '') {
      checkedCount++;
      if (isMonthOrWeekday(value)) {
        matchCount++;
      }
    }
  }

  return checkedCount > 0 && matchCount / checkedCount >= 0.8;
};

export const findMonthOrWeekField = (chartData: any[], chartAxes: any[]): string | null => {
  if (!Array.isArray(chartAxes) || !Array.isArray(chartData) || chartData.length === 0) {
    return null;
  }

  for (const axis of chartAxes) {
    if (axis.fields && Array.isArray(axis.fields)) {
      for (const field of axis.fields) {
        const fieldName = field.fieldname || field.name;
        if (fieldName && isMonthOrWeekField(chartData, fieldName)) {
          return fieldName;
        }
      }
    }
  }

  return null;
};

export const compareWithLogicalOrder = (
  valueA: any,
  valueB: any,
  ascending: boolean = true,
): number => {
  const aIndex = getLogicalIndex(valueA);
  const bIndex = getLogicalIndex(valueB);

  // Both values are months or weekdays - use logical order
  if (aIndex !== null && bIndex !== null) {
    // Ascending: Jan->Dec (0->11), Descending: Dec->Jan (11->0)
    return ascending ? aIndex - bIndex : bIndex - aIndex;
  }

  // Handle numeric values
  if (typeof valueA === 'number' && typeof valueB === 'number') {
    return ascending ? valueA - valueB : valueB - valueA;
  }

  // Handle string values (alphabetical)
  if (typeof valueA === 'string' && typeof valueB === 'string') {
    const comparison = valueA.localeCompare(valueB);
    return ascending ? comparison : -comparison;
  }

  // Handle null/undefined values
  if (valueA == null && valueB == null) return 0;
  if (valueA == null) return ascending ? 1 : -1;
  if (valueB == null) return ascending ? -1 : 1;

  // Fallback for other types
  return ascending ? valueA - valueB : valueB - valueA;
};

export const getDateDisplayName = (value: any): string => {
  if (typeof value === 'string' && isMonthOrWeekday(value)) {
    return value;
  }
  return String(value);
};
